import * as Logger from "js-logger";
import * as PIXI from 'pixi.js';
import {Spine} from 'pixi-spine';
import GameLoop from "slotworks/core/game-loop";
import ButtonElement from "slotworks/graphics/elements/button-element";
import Layers from "slotworks/graphics/layers/layers";
import RevolverBuilders from "slotworks/integration/revolver/revolver-builders";
import { RevolverInit, RevolverReady} from "slotworks/integration/revolver/revolver-setup";
import BuilderManager from "slotworks/server/builders/builder-manager";
import SlotWorks from "slotworks/slotworks";
import SpinRecordBuilder from "slotworks/integration/revolver/builders/records/spin-record-builder";
import HiddenButtonBehaviour from "slotworks/smart-button/behaviours/hidden-button-behaviour";
import SmartButton from "slotworks/smart-button/smart-button";
import { SmartButtonFlag } from "slotworks/smart-button/smart-button";
import StateMachine from "slotworks/state-machine/state-machine";
import StateRegister from "slotworks/state-machine/state-register";
import { EnableOnScreenErrors } from "slotworks/utils/debug";
import BannerComponent from "slotworks/components/banner/banner-component";
import deviceInfo from "slotworks/utils/device-info";
import revolverModel from "slotworks/integration/revolver/model/revolver-model";
import Components from "slotworks/components/components";
import graphicsManager from "slotworks/graphics/graphics-manager";
import campaignsModel from "slotworks/integration/revolver/model/campaigns-model";
import gameState from "slotworks/model/game-state";
import ShowWinLines from "slotworks/state-machine/standard/states/show-win-lines";
import BigWinComponent from "slotworks/components/bigwin/big-win-component";
import PromptComponent from "slotworks/components/prompt/prompt-component";
import FreespinButtonBehaviour from "slotworks/smart-button/behaviours/freespin-button-behaviour";
import { MultiStyleText } from "slotworks/graphics/pixi/pixi-multistyle-text";
import LinesComponent from "slotworks/components/lines/lines-component";
import SymbolComponent from "slotworks/components/matrix/symbol/symbol-component";
import ReelSpinner from "slotworks/components/matrix/reel/transition-behaviours/spin/reel-spinner";
import HarleyInit from "states/harley-init";
import BonusWheelTransition from "transitions/bonus-wheel-transition";
import IsBuyout from "decisions/is-buyout";
import BonusWheel from "states/bonus-wheel";
import BigWinsTransitions from "transitions/big-wins-transitions";
import WheelBonusResultBuilder from "integration/builders/wheel-bonus-result-builder";
import WheelBonusRecordBuilder from "integration/builders/wheel-bonus-record-builder";
import HarleyRecoveryRecordBuilder from "integration/builders/harley-recovery-record-builder";
import RecoveryBonusWheel from "states/recovery-bonus-wheel";
import RecoveryFreespinsWheel from "states/recovery-freespins-wheel";
import IsBonusWheel from "decisions/is-bonus-wheel";
import IsFreespinsWheel from "decisions/is-freespins-wheel";
import HarleyTotalWinTransitions from "transitions/total-win-transitions";
import FreespinsWheel from "states/freespins-wheel";
import FreespinsWheelRecordBuilder from "integration/builders/freespins-wheel-record-builder";
import HarleySpinRecordBuilder from "integration/builders/spin-record-builder";
import freespinsWheelResultBuilder from "integration/builders/freespins-wheel-result-builder";
import BetBarResizer from "slotworks/components/betbar/bet-bar-resizer";
import HarleyFreespinIdle from "states/freespins-idle";
import HarleyEnterRecoveryFreespins from "states/enter-recovery-freespins";
import HarleyEnterFreespins from "states/enter-freespins";
import FreespinsTransitions from "transitions/freespins-transitions";
import FreespinsNoBoostersTransitions from "transitions/freespins-no-boosters-transitions";
import HarleyExitFreespins from "states/exit-freespins";
import HarleyIdle from "states/idle";
import FreespinsBigWinsTransitions from "transitions/freespins-big-wins-transitions";
import HarleyRequestSpin from "states/request-spin";
import HarleyLoopWins from "states/loop-wins";
import BonusBuyTransitions from "transitions/bonus-buy-transitions";
import BonusWheelBigWins from "states/bonus-wheel-big-wins";
import BonusWheelPromptTransition from "transitions/bonus-wheel-prompt-transition";
import HarleyShowWinLines from "states/show-win-lines";
import BuyoutRecordBuilder from "integration/builders/buyout-record-builder";
import Buyout from "states/buyout";
import HarleyFreespinsRetrigger from "states/freespins-retrigger";
import FreespinsRetriggered from "decisions/freespins-retriggered";
import FreespinsRetriggerTransitions from "transitions/freespins-retrigger-transitions";
import soundManager from "slotworks/sound/sound-manager";
import FreespinsExitTransitions from "transitions/freespins-exit-transitions";
import HarleyLineWinTransitions from "transitions/line-win-transitions";
import HarleyInFreespins from "decisions/in-freespins";
import WheelStartTransitions from "transitions/wheel-start-transitions";
import HarleyShouldExitFreespins from "decisions/should-exit-freespins";
import WheelTurnsTransitions from "transitions/wheel-turns-transitions";

declare const __DEBUG__: boolean;
declare const require: any;
const assetConfig = require("./asset-config.json");
const gameConfig = require("./game-config.json");
const stateConfig = require("./state-config.json");
const soundConfig = require("./sound-config.json");
const componentConfig = require("./component-config.json");
const i18nConfig = require("./i18n-config.json");
const spineConfig = require("./spine-config.json");
const particleConfig = require("./particle-config.json");

require("../static/styles.scss");

if (__DEBUG__) {
    Logger.useDefaults();
}

RevolverInit();

const ready = () => {
    
    if (__DEBUG__) {
        (<any>window).Layers = Layers;
        (<any>window).Components = Components;
        (<any>window).deviceInfo = deviceInfo;
        (<any>window).graphics = graphicsManager;
        (<any>window).campaignsModel = campaignsModel;
        (<any>window).gameState = gameState;
        (<any>window).smart = SmartButton;
    }
    
    setupTransitions();

    SymbolComponent.RESET_ON_SCENE_CHANGE = false;
    MultiStyleText.autoShrink = true;
    MultiStyleText.debugOptions.autoShrink.enabled = true;
    soundManager.MUSIC_LAYERS = true;
    ReelSpinner.minRepeats = 25;

    revolverModel.addFreespinIdentifiers([
        "FREE_SPINS_12",
        "FREE_SPINS_8",
        "FREE_SPINS_15",
        "FREE_SPINS_10"
    ]);

    if (__DEBUG__) {
        GameLoop.showStats();
    }
    (GameLoop as any).fakeFps = 0;
    (GameLoop as any).frameSkipping = false;

    BetBarResizer.MOBILE_TEXTS_SCALE = true;
    RevolverReady();
    
    BannerComponent.loopTime = 10000000;
    ShowWinLines.MULTILINE_ONLY = false;
    FreespinButtonBehaviour.HIDDEN = false;
    
    for (let mask = 0; mask < SmartButtonFlag.MAX; mask++) {
        // Revolver does not have a stop auto spin button during freespins
        if ((mask & SmartButtonFlag.BONUS) > 0) {
            SmartButton.overrideBehaviour(mask, HiddenButtonBehaviour);
        }
    }
    // FooterComponent.layer = Layers.MatrixForeground;

    ButtonElement.DISABLED_TINT = 0xA9A9A9;
    ButtonElement.OVER_TINT = 0xd3d3d3;
    ButtonElement.DOWN_SCALE = 0.8;

    LinesComponent.DISABLED = true;

    // if (!deviceInfo.isDesktop) {
    //     Layers.LayersToReposition.push(
    //         Layers.MatrixForeground,
    //         Layers.MatrixBackground,
    //         Layers.CelebrationContent,
    //         Layers.CelebrationForeground,
    //         Layers.Custom.get("MatrixNeon"),
    //         Layers.Custom.get("Transition"),
    //         // Layers.Prompts,
    //         Layers.Banners,
    //     );
    //     Layers.LayersToReset.push(
    //         Layers.Background,
    //         Layers.Custom.get("Messages"),
    //         Layers.MatrixBackground,
    //         Layers.Custom.get("BonusBuy"),
    //         Layers.Custom.get("BonusBuyButton"),           
    //         Layers.Footer,
    //         Layers.CelebrationBackground,
    //         Layers.Custom.get("IntroSlides"),
    //         Layers.Custom.get("MatrixAnim"),
    //     );
    // }
};

const setupComms = () => {
    revolverModel.addFreespinIdentifiers(["FREE_SPINS"]);
    RevolverBuilders.init();
    BuilderManager.removeRecordBuilder(SpinRecordBuilder);
    BuilderManager.addRecordBuilder(HarleySpinRecordBuilder);
    BuilderManager.addRecordBuilder(HarleyRecoveryRecordBuilder);
    BuilderManager.addRecordBuilder(WheelBonusRecordBuilder);
    BuilderManager.addRecordBuilder(FreespinsWheelRecordBuilder);
    BuilderManager.addRecordBuilder(BuyoutRecordBuilder);
};

const setupStates = () => {
    StateRegister.add("harley-init", new HarleyInit(StateMachine));
    StateRegister.add("bonus-wheel", new BonusWheel(StateMachine));
    StateRegister.add("bonus-wheel-big-wins", new BonusWheelBigWins(StateMachine));
    StateRegister.add("freespins-wheel", new FreespinsWheel(StateMachine));
    StateRegister.add("recovery-bonus-wheel", new RecoveryBonusWheel(StateMachine));
    StateRegister.add("recovery-freespins-wheel", new RecoveryFreespinsWheel(StateMachine));
    StateRegister.add("harley-freespins-idle", new HarleyFreespinIdle(StateMachine));
    StateRegister.add("harley-enter-freespins", new HarleyEnterFreespins(StateMachine));
    StateRegister.add("harley-exit-freespins", new HarleyExitFreespins(StateMachine));
    StateRegister.add("harley-enter-recovery-freespins", new HarleyEnterRecoveryFreespins(StateMachine));
    StateRegister.add("harley-idle", new HarleyIdle(StateMachine));
    StateRegister.add("harley-request-spin", new HarleyRequestSpin(StateMachine));
    StateRegister.add("harley-loop-wins", new HarleyLoopWins(StateMachine));
    StateRegister.add("harley-show-win-lines", new HarleyShowWinLines(StateMachine));
    StateRegister.add("harley-freespins-retrigger", new HarleyFreespinsRetrigger(StateMachine));
    StateRegister.add("buyout", new Buyout(StateMachine));
    StateRegister.add("is-buyout", new IsBuyout());
    StateRegister.add("is-bonus-wheel", new IsBonusWheel());
    StateRegister.add("is-freespins-wheel", new IsFreespinsWheel());
    StateRegister.add("freespins-retriggered-check", new FreespinsRetriggered());
    StateRegister.add("harley-in-freespins", new HarleyInFreespins());
    StateRegister.add("harley-should-exit-freespins", new HarleyShouldExitFreespins());
};

const setupTransitions = () => {
    BigWinComponent.CLICK_TO_SKIP = true;
    PromptComponent.DEFAULT_TIME = 800;

    Layers.CelebrationContent.setTransitions(`line_win`, new HarleyLineWinTransitions());
    Layers.CelebrationContent.setTransitions("totalWin", new HarleyTotalWinTransitions());

    const bonusWheelPromptTransition = new BonusWheelPromptTransition();
    // Layers.Bonus.setTransitions("bonuswheelnew", new BonusWheelTransition());
    Layers.Prompts.setTransitions("freespinswin", new FreespinsTransitions());
    Layers.Prompts.setTransitions("freespinswin0boosters", new FreespinsNoBoostersTransitions());
    Layers.Prompts.setTransitions("bonuswheel1", bonusWheelPromptTransition);
    Layers.Prompts.setTransitions("bonuswheel2", bonusWheelPromptTransition);
    Layers.Prompts.setTransitions("bonuswheel3", bonusWheelPromptTransition);
    Layers.Prompts.setTransitions("bonusbuy", new BonusBuyTransitions());
    Layers.Prompts.setTransitions("retrigger", new FreespinsRetriggerTransitions());
    Layers.Prompts.setTransitions("freespinssummary", new FreespinsExitTransitions());
    Layers.CelebrationForeground.setTransitions("bigWin", new BigWinsTransitions());
    Layers.CelebrationForeground.setTransitions("freespinsBigWin", new FreespinsBigWinsTransitions());
    Layers.Custom.get("Transition").setTransitions("fs", new WheelStartTransitions());
    Layers.Custom.get("Transition").setTransitions("turns", new WheelTurnsTransitions());
};

setupComms();
setupStates();

SlotWorks.go(gameConfig, stateConfig, assetConfig, componentConfig, soundConfig, i18nConfig, spineConfig, particleConfig, ready);

if (__DEBUG__) {
    EnableOnScreenErrors();
}
